import React, { useMemo, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createPosition, getIconColor, getIconBorderColor, getLabelColor, isSatelliteMode } from "./helpers";
import { getPinColorIcon } from "components/explore/gmap/colorHelper";
import { getSelectedPinZIndex } from "utilities/appStatics";
import { InfoBox, Marker, OverlayView } from '@react-google-maps/api';
import { makeStyles } from "@material-ui/styles";
import { Constants } from 'utilities/constants';

const useStyles = makeStyles(theme => ({
  propCount: {
    // fontSize: label.fontSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  popupBubble: {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'translate(-50%, -100%)',
    /* Style the bubble. */
    backgroundColor: 'white',
    padding: 5,
    borderRadius: 5,
    fontFamily: 'sans-serif',
    overflowY: 'auto',
    maxHeight: 60,
    boxShadow: '0px 2px 10px 1px rgba(0, 0, 0, 0.5)',
    whiteSpace: 'nowrap'
  },
  popupBubbleAnchor: {
    position: "absolute",
    width: "100%",
    bottom: 8,
    left: 0,
    "&::after": {
      top: '100%',
      border: 'solid transparent',
      content: '""',
      height: 0,
      width: 0,
      position: 'absolute',
      pointerEvents: 'none',
      borderColor: 'transparent',
      borderBottomColor: 'white',
      borderLeftColor: 'white',
      borderWidth: '3px',
      marginLeft: '2px',
      boxShadow: '-6px 6px 1px -4px rgba(0,0,0,0.2), -5px 5px 2px -1px rgba(0,0,0,0.14), -4px 4px 5px 0px rgba(0,0,0,0.12)',
      transform: 'translate(-100%) rotate(-45deg) ',
      transformOrigin: '0 0'
    }
  }
}));
const popupContainerStyles = {
  cursor: "auto",
  height: 20,
  // position: 'absolute',
  /* The max width of the info window. */
  width: 200,
  backgroundColor: "red"
}
const dotSize = 14;

const addAlphaToHex = (colorString, alpha) => {
  let convertedString = colorString;
  if (colorString.startsWith('rgb')) {
    const splitArr = colorString.slice(colorString.indexOf('(') + 1, colorString.indexOf(')')).split(',');
    const componentToHex = c => {
      const int = parseInt(c.trim());
      const hex = int.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }
    convertedString = '#' + componentToHex(splitArr[0]) + componentToHex(splitArr[1]) + componentToHex(splitArr[2]);
  }
  return convertedString + alpha;
}

const PinWithTooltip = ({ isProperty, lat, lng, tooltip, hasPinDisplayType, mapType, selected, ...rest}) => {
  // const t0 = performance.now();
  const position = useMemo(() => createPosition(lat, lng), [lat, lng]);
  const [isHovered, setIsHovered] = useState(false);

  const classes = useStyles();

  const scale = selected ? 1.3 : 1;
  const clusterPadding = isProperty ? 0 : 4;
  const height = 24 + 2 * clusterPadding;
  // const t1 = performance.now()
  // console.log(t1 - t0)
  return <>
    <Pin
      setIsHovered={setIsHovered}
      position={position}
      isProperty={isProperty}
      hasPinDisplayType={hasPinDisplayType}
      mapType={mapType}
      selected={selected}
      scale={scale}
      clusterPadding={clusterPadding}
      height={height}
      {...rest}
    />
    {isHovered && !selected &&
      <InfoBox
        position={position}
        options={{
          closeBoxURL: '',
          alignBottom: true,
          pixelOffset: {
            width: 0,
            height: -(!hasPinDisplayType && isProperty ? dotSize : height) * scale
          },
          boxStyle: { ...popupContainerStyles, overflow: 'initial' },
          disableAutoPan: true
        }}
      >
        <div className={classes.popupBubbleAnchor}>
          <div className={classes.popupBubble}>
            {tooltip}
          </div>
        </div>
      </InfoBox>
    }
    {selected &&
      <OverlayView
        mapPaneName='floatPane'
        position={position}
      >
        <div className={classes.popupBubbleAnchor} style={{ bottom: 8 + (!hasPinDisplayType && isProperty ? dotSize : height) * scale, transform: 'scale(1.1)' }}>
          <div className={classes.popupBubble} style={{ padding: 8 }}>
            {tooltip}
          </div>
        </div>
      </OverlayView>
    }
  </>
}
const isIPhone = /iPhone/i.test(navigator.userAgent); // Test if user agent is iPhone, don't show box-shadow if so (workaround for positioning issue on iPhones)

const Pin = React.memo(({ value, text, isProperty, setIsHovered, position, hasPinDisplayType, pinDisplayType, hasNonNumericPinDisplayType, pinColorIds, valueColor,
  mapType, onClick, selected: originalSelected, isHighlighted, scale, clusterPadding, height, id, isDrawing, visible }) => {
  const selected = originalSelected;
  const handleClick = useCallback(e => {
    onClick(e, id);
  }, [id, onClick])
  const isSatellite = useMemo(() => isSatelliteMode(mapType), [mapType]);
  const iconColor = useMemo(() => getIconColor(selected, isHighlighted, pinColorIds, mapType), [pinColorIds, selected, isHighlighted, mapType]);
  const iconBorderColor = useMemo(() => getIconBorderColor(selected, isHighlighted, hasPinDisplayType, valueColor, pinColorIds, mapType), [selected, isHighlighted, hasPinDisplayType, valueColor, pinColorIds, mapType]);

  const label = useMemo(() => {
    let t = `${text}`;
    let color;

    if (hasPinDisplayType && (pinDisplayType.dataType === 'number' || t === '-n/a-')) {
      color = 'white';
    } else {
      color = getLabelColor(pinColorIds, mapType, selected, isHighlighted);
    }

    return {
      text: t,
      color,
      fontWeight: "bold",
      fontSize: "13px"
    };
  }, [hasPinDisplayType, pinColorIds, mapType, text, selected, isHighlighted]);

  const width = (label.text.length * 7 + 2 * clusterPadding + 14);

  const iconString = useMemo(() => {
    return 'data:image/svg+xml,' + encodeURIComponent(isProperty
      ? `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'><foreignObject width='100%' height='100%' style='overflow: visible;'><div xmlns='http://www.w3.org/1999/xhtml' style='display: flex;justify-content: center;'><div style='background: ${(isHighlighted || hasNonNumericPinDisplayType || !hasPinDisplayType) ? iconColor : valueColor};width: min-content;color: ${label.color};border: ${selected ? '2px solid #e58c00' : isSatellite ? '1px solid yellow' : 'none'};border-radius: 4px;padding: 1px 4px;font-size: 12px;font-family: Helvetica;white-space: nowrap;margin-top: 3px;box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12);'><span>${label.text}</span></div></div></foreignObject></svg>`
      : `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
          <foreignObject width="100%" height="100%" style="overflow: visible;">
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style="
                display: flex;
                justify-content: center;
              "
            >
              <div
                style="
                  padding: ${clusterPadding}px;
                  background-color: ${addAlphaToHex(iconBorderColor, selected || isHighlighted ? 'cc' : '80')};
                  border-radius: ${label.text.length > 1 ?  `50%` : '12px'};
                  box-shadow: ${isIPhone ? 'none' : '0px 5px 1px -2px rgba(0,0,0,0.2), 0px 4px 2px 0px rgba(0,0,0,0.14), 0px 3px 5px 0px rgba(0,0,0,0.12)'};
                  margin-top: 3px;
                "
              >
                <div
                  style="
                    background: ${hasPinDisplayType && !isHighlighted ? valueColor : iconColor};
                    width: min-content;
                    color: ${label.color};
                    border-radius: ${label.text.length > 1 ?  `50%` : '8px'};
                    padding: 1px ${label.text.length > 1 ?  '4' : '5'}px;
                    font-size: 12px;
                    font-family: Helvetica;
                    white-space: nowrap;
                  "
                >
                  <span>${label.text}</span>
                </div>
              </div>
            </div>
          </foreignObject>
        </svg>`
  )}, [isSatellite, isProperty, clusterPadding, valueColor, height, iconColor, iconBorderColor, label.color, label.text, hasPinDisplayType, hasNonNumericPinDisplayType, selected, isHighlighted, width]);

  const dotImage = useMemo(() => {
    const color = isHighlighted
      ? Constants.ViewsPropertyDataPinColor.YELLOW
      : selected
        ? Constants.ViewsPropertyDataPinColor.ORANGE // Selected always orange
        : value == null
          ? Constants.ViewsPropertyDataPinColor.GRAY // With label, null value
          : label.color === 'black'
            ? pinColorIds?.[mapType] ?? Constants.ViewsPropertyDataPinColor.PURPLE // Satellite mode
            : pinColorIds?.[mapType] ?? Constants.ViewsPropertyDataPinColor.GREEN // Default
          
    return getPinColorIcon(color);
  }, [label.color, mapType, pinColorIds, isHighlighted, selected, value]);
  
  return <Marker
    position={position}
    options={{
      optimized: true
    }}
    zIndex={selected || isHighlighted ? getSelectedPinZIndex() : hasPinDisplayType && value != null ? 999 : 998}
    icon={isProperty && (!hasPinDisplayType || value == null ) // Use dots when no label selected, or if label but value is null
      ? {
          scaledSize: scale > 1 ? { width: dotSize * scale, height: dotSize * scale } : undefined, // No need to scale if value = 1
          url: dotImage
        }
      : { scaledSize: { width: width * scale, height: height * scale },
          url: iconString
    }}
    onMouseOver={() => setIsHovered(true)}
    onMouseOut={() => setIsHovered(false)}
    onClick={handleClick}
    clickable={!isDrawing}
    visible={visible}
  />
});

function mapState(state, ownProps) {
  const { exploreMap, exploreData, exploreViews } = state;
  return {
    selected: exploreData.selectedIdsContext?.ids?.find(el => el.objectId === ownProps.objectId && el.clusterID === ownProps.id) ?? false,
    mapType: exploreMap.mapType,
    enableRounding: exploreMap.markerOptions.enableRounding,
    isDrawing: exploreViews.activeDrawingObject != null || exploreViews.activeEditingObject != null 
  };
}

export default connect(mapState)(PinWithTooltip);
